<script lang="ts">
import { defineComponent, ref, onMounted, onUnmounted } from "vue";
import { initGame } from "./game/game";
import { ScreenOrientation } from "./types/screen-orientation";
import { GraphicsSettingsManager } from "./game/config/graphics";
import { GraphicsQuality, GRAPHICS_QUALITY_OPTIONS } from "./game/config/types";
import { TANK_MODELS } from "./game/tanks/TankModel";
import { TankModelSelector } from "./game/tanks/TankModelSelector";
import "./footerStyle.css";

export default defineComponent({
  name: "App",
  setup() {
    const gameStarted = ref(false);
    const gameCanvas = ref<HTMLCanvasElement | null>(null);
    const isMobile = ref(detectMobile());
    const shadowsEnabled = ref(false);
    const showChangelog = ref(false);
    const graphicsQuality = ref<GraphicsQuality | "auto">("medium");
    const graphicsManager = GraphicsSettingsManager.getInstance();
    const isLoading = ref(false);
    const loadingProgress = ref(0);
    const loadingStatus = ref("Initializing...");
    const selectedTankModel = ref(localStorage.getItem("selectedTankModel") || "standard");
    const tankModels = ref(Object.values(TANK_MODELS));
    const username = ref(localStorage.getItem("username") || "");
    const usernameError = ref("");

    // More accurate mobile detection that matches MobileController.ts
    function detectMobile(): boolean {
      // Check for touch capability
      const hasTouchScreen =
        "ontouchstart" in window || navigator.maxTouchPoints > 0 || (navigator as any).msMaxTouchPoints > 0;

      // Check for mobile user agent
      const isMobileUserAgent = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      );

      // Check for small screen size
      const isSmallScreen = window.innerWidth <= 1024 || window.innerHeight <= 768;

      // We need both a mobile user agent AND either touch capability or small screen
      return isMobileUserAgent && (hasTouchScreen || isSmallScreen);
    }

    function handleResize() {
      isMobile.value = detectMobile();
      console.log("[App] Mobile detection on resize:", isMobile.value);
    }

    function selectTankModel(modelId: string) {
      selectedTankModel.value = modelId;
      localStorage.setItem("selectedTankModel", modelId);
    }

    async function startGame() {
      // Validate username
      if (!username.value.trim()) {
        usernameError.value = "Username is required to start the game";
        return;
      }

      // Save username to localStorage
      localStorage.setItem("username", username.value);
      usernameError.value = "";

      isLoading.value = true;
      loadingProgress.value = 0;
      gameStarted.value = true;

      // Lock screen orientation on mobile devices
      if ("orientation" in screen && "lock" in (screen.orientation as any)) {
        try {
          (screen.orientation as any).lock("landscape").catch(() => {
            // Silently fail if orientation lock is not supported
          });
        } catch {
          // Fallback for browsers that don't support the promise-based API
        }
      }

      const updateProgress = (progress: number, status?: string) => {
        loadingProgress.value = Math.min(progress, 100);
        if (status) loadingStatus.value = status;
      };

      const waitForFreshPing = (timeoutMs = 1200) =>
        new Promise<void>((resolve) => {
          const socket = window.socket;

          window.currentPing = undefined;
          if (!socket?.connected) {
            resolve();
            return;
          }

          let resolved = false;
          let timeoutId: number | undefined;
          let handlePong: (data: { clientTime?: number }) => void;

          const finish = () => {
            if (resolved) return;
            resolved = true;
            if (timeoutId !== undefined) {
              window.clearTimeout(timeoutId);
            }
            socket.off("pong", handlePong);
            resolve();
          };

          handlePong = (data: { clientTime?: number }) => {
            const clientTime = typeof data?.clientTime === "number" ? data.clientTime : Date.now();
            window.currentPing = Date.now() - clientTime;
            finish();
          };

          socket.on("pong", handlePong);
          timeoutId = window.setTimeout(finish, timeoutMs);
          socket.emit("ping", { time: Date.now() });
        });

      try {
        // Wait for canvas to be mounted
        updateProgress(5, "Preparing canvas...");
        await new Promise((resolve) => setTimeout(resolve, 100));
        updateProgress(10);

        if (!gameCanvas.value) {
          throw new Error("Canvas not found");
        }

        // Initialize graphics settings
        console.log("[App] Starting game with graphics quality:", graphicsQuality.value);
        updateProgress(15, "Configuring graphics...");

        if (graphicsQuality.value === "auto") {
          console.log("[App] Starting auto detection of graphics settings");
          await graphicsManager.startAutoDetection();
        } else {
          console.log("[App] Applying graphics preset:", graphicsQuality.value);
          await graphicsManager.applyPreset(graphicsQuality.value);
        }
        updateProgress(25, "Connecting to server...");

        // Initialize game with selected tank model
        // This waits for socket connection + environment initialization
        console.log("[App] Selected tank model before game init:", selectedTankModel.value);
        await initGame(gameCanvas.value, {
          shadowsEnabled: shadowsEnabled.value,
          selectedTankModel: selectedTankModel.value,
          username: username.value,
          isMobile: isMobile.value,
        });
        updateProgress(75, "Loading environment...");

        // Wait for initial frames to stabilize (warm-up period)
        console.log("[App] Waiting for game warm-up...");
        updateProgress(80, "Warming up engine...");
        await new Promise((resolve) => setTimeout(resolve, 1000));
        updateProgress(90, "Stabilizing FPS...");
        await new Promise((resolve) => setTimeout(resolve, 1000));
        updateProgress(98, "Almost ready...");
        await waitForFreshPing();

        // Final stabilization
        await new Promise((resolve) => setTimeout(resolve, 300));
        updateProgress(100, "Ready!");

        // Complete loading after brief delay for smooth transition
        setTimeout(() => {
          isLoading.value = false;
          console.log("[App] Loading complete, game ready");
        }, 300);
      } catch (error) {
        console.error("[App] Error during game initialization:", error);
        isLoading.value = false;
        gameStarted.value = false;
      }
    }

    onMounted(() => {
      window.addEventListener("resize", handleResize);
    });

    onUnmounted(() => {
      window.removeEventListener("resize", handleResize);
    });

    function toggleChangelog() {
      showChangelog.value = !showChangelog.value;
    }

    return {
      gameStarted,
      gameCanvas,
      isMobile,
      shadowsEnabled,
      startGame,
      showChangelog,
      toggleChangelog,
      graphicsQuality,
      GRAPHICS_QUALITY_OPTIONS,
      isLoading,
      loadingProgress,
      loadingStatus,
      selectedTankModel,
      tankModels,
      selectTankModel,
      username,
      usernameError,
      handleResize,
    };
  },
});
</script>

<template>
  <div class="app-container no-select" :class="{ isMobile: isMobile }">
    <div v-if="!gameStarted && !showChangelog" class="start-screen">
      <div class="content">
        <h1>TankNarok.online</h1>
        <p class="description">
          Multiplayer tank battle game where you compete against other players in real-time combat. Maneuver your tank,
          avoid enemy fire, and destroy your opponents. Have fun!
        </p>

        <div class="tank-selection">
          <h2>Select Your Tank:</h2>
          <div class="tank-models-grid">
            <div
              v-for="model in tankModels"
              :key="model.id"
              class="tank-model-item"
              :class="{ selected: selectedTankModel === model.id }"
              @click="selectTankModel(model.id)"
            >
              <div class="tank-model-preview" :style="{ backgroundColor: '#' + model.colors.body.toString(16) }"></div>
              <div class="tank-model-info">
                <h3>{{ model.name }}</h3>
                <div class="tank-stats">
                  <div class="stat-bar">
                    <span>Speed</span>
                    <div class="stat-bar-bg">
                      <div class="stat-bar-fill" :style="{ width: (model.stats.maxSpeed * 100) / 10 + '%' }"></div>
                    </div>
                  </div>
                  <div class="stat-bar">
                    <span>Armor</span>
                    <div class="stat-bar-bg">
                      <div class="stat-bar-fill" :style="{ width: (model.stats.armor * 100) / 15 + '%' }"></div>
                    </div>
                  </div>
                  <div class="stat-bar">
                    <span>Health</span>
                    <div class="stat-bar-bg">
                      <div class="stat-bar-fill" :style="{ width: (model.stats.health * 100) / 140 + '%' }"></div>
                    </div>
                  </div>
                  <div class="stat-projectile">
                    <span>Ammo type</span>
                    <div>{{ model.projectilesConfig.TYPE }}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="username-container">
          <label for="username" class="username-label">Username: </label>
          <input
            id="username"
            v-model="username"
            class="username-input"
            placeholder="Enter your username"
            required
            maxlength="16"
          />
          <div v-if="usernameError" class="error-message">{{ usernameError }}</div>
          <button @click="startGame" class="start-button" role="button">Start Game</button>
        </div>

        <div class="side-by-side-container">
          <div class="badges-container">
            <h2 class="badges-title">Featured on:</h2>
            <a
              href="https://grokade.com/game/your-game-slug"
              target="_blank"
              rel="noopener noreferrer"
              title="Featured on Grokade"
            >
              <img
                src="https://grokade.com/images/badges/grokade-basic-purple.svg"
                alt="Featured on Grokade"
                width="180"
                height="60"
                style="padding: 5px"
              />
            </a>
          </div>

          <div class="settings">
            <h2 class="badges-title">Settings:</h2>
            <div class="settings-grid">
              <div class="setting-item">
                <label class="select-label">
                  <span>Graphics Quality: </span>
                  <select v-model="graphicsQuality" class="quality-select">
                    <option v-for="option in GRAPHICS_QUALITY_OPTIONS" :key="option.value" :value="option.value">
                      {{ option.label }}
                    </option>
                  </select>
                  <p class="setting-description">
                    {{ GRAPHICS_QUALITY_OPTIONS.find((opt) => opt.value === graphicsQuality)?.description }}
                  </p>
                </label>
              </div>
            </div>
          </div>
        </div>

        <div class="controls">
          <h2>Controls:</h2>
          <div class="controls-grid">
            <div class="control-section">
              <h3>Movement:</h3>
              <ul>
                <li><strong>W</strong>Move forward</li>
                <li><strong>S</strong>Move backward</li>
                <li><strong>A</strong> / <strong>←</strong>Turn left</li>
                <li><strong>D</strong> / <strong>→</strong>Turn right</li>
                <li><strong>↑</strong>Barrel up</li>
                <li><strong>↓</strong>Barrel down</li>
                <li><strong>Shift</strong>Handbrake</li>
              </ul>
            </div>
            <div class="control-section">
              <h3>Combat:</h3>
              <ul>
                <li><strong>Click</strong> / <strong>Space</strong>Fire</li>
                <li><strong>Scroll</strong>Zoom in/out</li>
              </ul>
              <p class="note">No damage in respawn zone.</p>
              <p class="note">Full green tanks are bots.</p>
            </div>
            <div class="control-section">
              <h3>HUD:</h3>
              <ul>
                <li><strong>Tab</strong>Show/Hide Battle Stats</li>
              </ul>
            </div>
          </div>
        </div>

        <p v-if="isMobile" class="mobile-notice">
          For the best experience, please rotate your device to landscape mode
        </p>

        <div class="footer-links">
          <a href="#" @click.prevent="toggleChangelog" class="changelog-link">Changelog</a>
        </div>
      </div>
    </div>

    <div v-if="!gameStarted && showChangelog" class="start-screen">
      <div class="content changelog-content">
        <h1>Changelog</h1>
        <div class="changelog-container">
          <div class="changelog-entry">
            <h3>Version 1.4.0 <span class="changelog-date">- November 23, 2025</span></h3>
            <ul>
              <li>
                Implemented several performance optimizations, both server and client side:
                <ul>
                  <li>cache materials</li>
                  <li>switch off edges for low quality</li>
                  <li>single listener for graphics settings</li>
                  <li>no more debug logs for prod</li>
                  <li>grassrendered to avoid multiple reinstances on LOD changes</li>
                  <li>use shared array to keep update player position instead of creating it every frame</li>
                  <li>added chunks throttling and leaks prevention</li>
                  <li>fixed ETag cache busting for index.html</li>
                  <li>envoirenement chunks loading is now throttled</li>
                </ul>
              </li>
              <li>Inactivity kick switched off for bots, added kicked-hud for player (5min)</li>
            </ul>
          </div>

          <div class="changelog-entry">
            <h3>Version 1.3.7 <span class="changelog-date">- April 01, 2025</span></h3>
            <ul>
              <li>Implemented papercraft look for trees</li>
              <li>Changed ambient light from 0.8 to 0.9 for softer look</li>
            </ul>
          </div>

          <div class="changelog-entry">
            <h3>Version 1.3.6 <span class="changelog-date">- March 19, 2025</span></h3>
            <ul>
              <li>Added mobile controls support and start screen optimizations for mobile</li>
              <li>Added usernames to game</li>
              <li>Started implementation of different explosion types for different projectiles (not active)</li>
              <li>Modified grass parameters for smoother appearance</li>
            </ul>
          </div>

          <div class="changelog-entry">
            <h3>Version 1.3.5 <span class="changelog-date">- March 18, 2025</span></h3>
            <ul>
              <li>Improved bot explosion handling and effects</li>
              <li>Implemented different projectiles for different tank types</li>
              <li>Added tank model selector to main screen with 3 different tanks</li>
              <li>Reduced map and skybox size</li>
              <li>Added loading screen</li>
              <li>Implemented per-level graphic settings</li>
              <li>Optimized grass renderer performance</li>
              <li>Fixed duplicated meshes issue</li>
              <li>Improved visibility of other players' tanks</li>
            </ul>
          </div>

          <div class="changelog-entry">
            <h3>Version 1.3.0 <span class="changelog-date">- March 15, 2025</span></h3>
            <ul>
              <li>Added two new projectiles (bolt, laser) with unique explosion effects</li>
              <li>Improved bot explosion handling and effects</li>
              <li>Added online and bots count stats to HUD</li>
              <li>Fixed player tank mesh duplication</li>
            </ul>
          </div>

          <div class="changelog-entry">
            <h3>Version 1.2.9 <span class="changelog-date">- March 14, 2025</span></h3>
            <ul>
              <li>Added ping feature with HUD display</li>
              <li>Optimized lighting and shadow system with toggle option</li>
              <li>Enhanced grass rendering:</li>
              <ul>
                <li>Reduced mud patches and optimized appearance</li>
                <li>Improved performance with limited animations</li>
              </ul>
            </ul>
          </div>

          <div class="changelog-entry">
            <h3>Version 1.2.8 <span class="changelog-date">- March 13, 2025</span></h3>
            <ul>
              <li>Implemented comprehensive cleanup systems for smoke effects, tank meshes, and collision data</li>
              <li>Added centralized event management system</li>
              <li>Improved bot management and performance optimization</li>
            </ul>
          </div>

          <div class="changelog-entry">
            <h3>Version 1.2.7 <span class="changelog-date">- March 12, 2025</span></h3>
            <ul>
              <li>Enhanced grass renderer with LOD and distance improvements</li>
              <li>Added billboards and started training area implementation</li>
              <li>Improved explosion effects and memory management</li>
              <li>Fixed gameplay mechanics including collision detection and damage system</li>
            </ul>
          </div>

          <div class="changelog-entry">
            <h3>Version 1.2.6 <span class="changelog-date">- March 11, 2025</span></h3>
            <ul>
              <li>Added training track (inactive) and smoke effects for damaged tanks</li>
              <li>Updated damage configuration and wheel mechanics</li>
              <li>Implemented automatic removal of inactive players</li>
              <li>Optimized map size and environmental elements</li>
            </ul>
          </div>

          <div class="changelog-entry">
            <h3>Version 1.2.5 <span class="changelog-date">- March 10, 2025</span></h3>
            <ul>
              <li>Added antenna to tank models</li>
              <li>Enhanced bot AI with improved wandering and targeting</li>
              <li>Optimized grass rendering and fixed spawn issues</li>
            </ul>
          </div>

          <div class="changelog-entry">
            <h3>Version 1.2.0 <span class="changelog-date">- March 9, 2025</span></h3>
            <ul>
              <li>Added billboard advertising system</li>
              <li>Enhanced environmental features:</li>
              <ul>
                <li>Improved cloud and grass rendering</li>
                <li>Added environmental fog and GLSL interactivity</li>
              </ul>
              <li>Implemented start screen and analytics features</li>
            </ul>
          </div>

          <div class="changelog-entry">
            <h3>Version 1.1.0 <span class="changelog-date">- March 3-8, 2025</span></h3>
            <ul>
              <li>Implemented tank collision system and health display</li>
              <li>Added respawn zones and movement controller</li>
              <li>Enhanced multiplayer synchronization</li>
              <li>Improved map generation and physics</li>
            </ul>
          </div>

          <div class="changelog-entry">
            <h3>Version 1.0.0 <span class="changelog-date">- February 28, 2025</span></h3>
            <ul>
              <li>Initial release with core features:</li>
              <ul>
                <li>Tank movement and shooting mechanics</li>
                <li>Basic environment with destructible trees</li>
                <li>Consolidated configuration system</li>
              </ul>
            </ul>
          </div>
        </div>

        <button @click="toggleChangelog" class="back-button" role="button">Back to Main Menu</button>
      </div>
    </div>

    <div v-else class="game-container">
      <div v-if="isLoading" class="loading-screen">
        <div class="loading-content">
          <h2>Loading Game...</h2>
          <div class="progress-bar">
            <div class="progress-fill" :style="{ width: loadingProgress + '%' }"></div>
          </div>
          <p class="loading-text">{{ loadingProgress }}%</p>
          <p class="loading-status">{{ loadingStatus }}</p>
        </div>
      </div>
      <canvas ref="gameCanvas"></canvas>
    </div>
  </div>

  <footer>
    <div class="footer-container">
      <div class="footer-border">
        <p class="footer-copyright">
          &copy; TankNarok.online | Made with ❤️ by
          <a href="https://rpaby.pw/" target="_blank" class="author-link">rpaby.pw</a>.
        </p>
      </div>
    </div>
  </footer>
</template>

<style>
html,
body {
  overflow-y: auto !important;
}
</style>

<style scoped>
.app-container {
  width: 100%;
  display: flex;
  flex-direction: column;
}

.game-container {
  width: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
}

canvas {
  width: 100%;
  height: 100%;
  display: block;
  position: absolute;
  top: 0;
  left: 0;
  object-fit: cover;
  touch-action: none;
}

.start-screen {
  width: 100%;
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  background: linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%);
  color: white;
  padding: 1rem;
}

.content {
  width: 100%;
  max-width: 800px;
  padding: 1.5rem;
  text-align: center;
  margin: 1rem auto;
}

h1 {
  font-size: clamp(2rem, 6vw, 4rem);
  margin-bottom: 1.5rem;
  color: #4caf50;
  text-shadow: 0 0 10px rgba(76, 175, 80, 0.3);
}

.description {
  font-size: clamp(1rem, 2.5vw, 1.2rem);
  line-height: 1.6;
  margin-bottom: 2rem;
  color: #e0e0e0;
  max-width: 90%;
  margin-left: auto;
  margin-right: auto;
}

.side-by-side-container {
  display: flex;
  justify-content: space-between;
  gap: 2rem;
  margin-bottom: 1rem;
  width: 100%;
}

.badges-container,
.settings {
  flex: 1;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 8px;
  padding: 1rem;
  height: 100%;
}

.badges-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
}

.badges-title {
  font-size: 1.2rem;
  margin-top: 0;
}

.controls,
.settings {
  background: rgba(0, 0, 0, 0.2);
  padding: 1.5rem;
  border-radius: 8px;
  margin-bottom: 2rem;
  text-align: left;
}

.controls-grid,
.settings-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
}

.control-section h3 {
  color: #4caf50;
  margin-bottom: 0.75rem;
  font-size: 1.2rem;
}

.controls ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.controls li {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin: 0.75rem 0;
}

.controls li strong {
  display: inline-block;
  min-width: 3.5rem;
  padding: 0.35rem 0.5rem;
  background: rgba(76, 175, 80, 0.15);
  border-radius: 4px;
  text-align: center;
  margin-right: 0.5rem;
}

.note {
  margin-top: 1rem;
  font-size: 0.9rem;
  color: #888;
  font-style: italic;
}

@media (max-width: 768px) {
  .controls li {
    margin: 1rem 0;
  }

  .controls li strong {
    min-width: 4rem;
    padding: 0.5rem;
    font-size: 1.1rem;
  }

  .note {
    text-align: center;
    margin-top: 1.5rem;
  }
}

.start-button {
  background: #4caf50;
  color: white;
  border: none;
  padding: 1rem 2rem;
  font-size: 1.2rem;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  margin: 1rem auto;
  display: inline-block;
  min-width: 200px;
  touch-action: manipulation;
  min-height: 44px;
}

.start-button:hover {
  background: #45a049;
  transform: translateY(-2px);
}

.start-button:active {
  transform: translateY(0);
}

.mobile-notice {
  color: #ff9800;
  margin-top: 1rem;
  font-size: 0.9rem;
  padding: 0.5rem;
  background: rgba(255, 152, 0, 0.1);
  border-radius: 4px;
}

/* Mobile Optimizations */
@media (max-width: 768px) {
  .content {
    padding: 1rem;
  }

  .controls {
    padding: 1rem;
    margin-bottom: 1.5rem;
  }

  .controls-grid {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }

  .control-section h3 {
    font-size: 1.1rem;
    margin-bottom: 1rem;
  }

  .controls li {
    margin: 0.5rem 0;
    font-size: 1rem;
  }

  .controls li strong {
    min-width: 3rem;
    font-size: 1rem;
    padding: 0.25rem 0.5rem;
    background: rgba(76, 175, 80, 0.1);
    border-radius: 4px;
    text-align: center;
  }

  .start-button {
    padding: 0.75rem 1.5rem;
    font-size: 1.1rem;
    width: 100%;
    max-width: 300px;
  }
}

/* Landscape Mode Optimizations */
@media (max-height: 500px) and (orientation: landscape) {
  .start-screen {
    align-items: flex-start;
  }

  .content {
    padding: 0.75rem;
    max-width: 100%;
  }

  h1 {
    font-size: 1.5rem;
    margin-bottom: 0.5rem;
  }

  .description {
    font-size: 0.9rem;
    margin-bottom: 1rem;
  }

  .controls {
    margin-bottom: 1rem;
  }

  .controls-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
  }

  .start-button {
    margin: 0.5rem auto;
    padding: 0.5rem 1rem;
    font-size: 1rem;
  }
}

/* Toggle Switch Styles */
.toggle-label {
  display: flex;
  align-items: center;
  gap: 1rem;
  cursor: pointer;
  margin: 0.75rem 0;
}

.toggle-switch {
  position: relative;
  display: inline-block;
  width: 50px;
  height: 24px;
}

.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.toggle-slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #333;
  transition: 0.4s;
  border-radius: 24px;
}

.toggle-slider:before {
  position: absolute;
  content: "";
  height: 16px;
  width: 16px;
  left: 4px;
  bottom: 4px;
  background-color: white;
  transition: 0.4s;
  border-radius: 50%;
}

input:checked + .toggle-slider {
  background-color: #4caf50;
}

input:checked + .toggle-slider:before {
  transform: translateX(26px);
}

.toggle-status {
  min-width: 30px;
  font-size: 0.9rem;
}

.setting-item {
  margin: 0.75rem 0;
}

/* Changelog Styles */
.changelog-content {
  text-align: left;
}

.changelog-container {
  background: rgba(0, 0, 0, 0.2);
  padding: 1.5rem;
  border-radius: 8px;
  margin: 1.5rem 0;
  max-height: 60vh;
  overflow-y: auto;
}

.changelog-entry {
  margin-bottom: 2rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  padding-bottom: 1.5rem;
}

.changelog-entry:last-child {
  border-bottom: none;
  margin-bottom: 0;
  padding-bottom: 0;
}

.changelog-entry h3 {
  color: #4caf50;
  margin-bottom: 1rem;
  font-size: 1.3rem;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.changelog-date {
  color: #aaa;
  font-size: 0.9rem;
  font-weight: normal;
}

.changelog-entry ul {
  list-style-type: disc;
  padding-left: 1.5rem;
  margin-bottom: 0.5rem;
}

.changelog-entry li {
  margin: 0.5rem 0;
  line-height: 1.5;
  color: #e0e0e0;
}

.back-button {
  background: #4caf50;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  font-size: 1.1rem;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  margin: 1rem 0;
  display: inline-block;
  min-width: 200px;
}

.back-button:hover {
  background: #45a049;
  transform: translateY(-2px);
}

.footer-links {
  margin-top: 2rem;
  font-size: 0.9rem;
}

.changelog-link {
  color: #4caf50;
  text-decoration: none;
  padding: 0.5rem;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.changelog-link:hover {
  text-decoration: underline;
  background: rgba(76, 175, 80, 0.1);
}

/* High DPI Screen Optimizations */
@media (-webkit-min-device-pixel-ratio: 2), (min-resolution: 192dpi) {
  .controls li strong {
    padding: 0.75rem 1.5rem;
    font-size: 1.1rem;
    width: 100%;
    max-width: 300px;
  }
}

/* Loading Screen Styles */
.loading-screen {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.loading-content {
  text-align: center;
  color: white;
  padding: 2rem;
}

.loading-content h2 {
  font-size: 2rem;
  margin-bottom: 1.5rem;
  color: #4caf50;
}

.progress-bar {
  width: 300px;
  height: 20px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  overflow: hidden;
  margin: 1rem auto;
}

.progress-fill {
  height: 100%;
  background: #4caf50;
  transition: width 0.3s ease;
}

.loading-text {
  font-size: 1.2rem;
  color: #4caf50;
}

.loading-status {
  font-size: 0.9rem;
  color: #888;
  margin-top: 0.5rem;
  font-style: italic;
}

/* Tank Selection Styles - Preserving Original Look */
.tank-selection {
  margin: 1.5rem 0;
  width: 100%;
}

.tank-models-grid {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 1rem;
  margin-top: 1rem;
  width: 100%;
}

.tank-model-item {
  display: flex;
  flex-direction: column;
  width: 200px;
  background-color: rgba(0, 0, 0, 0.2);
  border-radius: 8px;
  padding: 1rem;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 2px solid transparent;
}

.tank-model-item:hover {
  background-color: rgba(0, 0, 0, 0.3);
  transform: translateY(-3px);
}

.tank-model-item.selected {
  border-color: #4caf50;
  background-color: rgba(0, 0, 0, 0.4);
}

.tank-model-preview {
  height: 60px;
  border-radius: 4px;
  margin-bottom: 0.5rem;
  position: relative;
}

.tank-model-info h3 {
  margin: 0 0 0.5rem 0;
  font-size: 1rem;
}

.tank-stats {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.stat-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 0.8rem;
}

.stat-bar span {
  width: 60px;
}

.stat-bar-bg {
  flex-grow: 1;
  height: 8px;
  background-color: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  overflow: hidden;
}

.stat-bar-fill {
  height: 100%;
  background-color: #4caf50;
  border-radius: 4px;
}

/* Mobile Optimizations */
@media (max-width: 768px) {
  .tank-models-grid {
    gap: 0.75rem;
    padding: 0.5rem;
  }

  .tank-model-item {
    width: 180px;
    /* Slightly smaller on mobile but keeping proportions */
  }

  .tank-model-preview {
    height: 54px;
  }
}

/* Landscape Mode Optimizations */
@media (max-height: 500px) and (orientation: landscape) {
  .tank-models-grid {
    gap: 0.75rem;
    padding: 0.5rem;
  }

  .tank-model-item {
    width: 160px;
    /* Slightly smaller in landscape */
  }

  .tank-model-preview {
    height: 48px;
    /* Keeping aspect ratio */
  }
}

/* Username Styles */
.username-container {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 20px auto;
  width: 100%;
  max-width: 600px;
}

.username-label {
  margin-bottom: 8px;
  font-size: 18px;
  font-weight: bold;
  width: 100%;
  text-align: right;
}

.username-input {
  flex: 1;
  min-width: 200px;
  padding: 16px;
  border: 2px solid #444;
  border-radius: 6px;
  background-color: rgba(32, 32, 32, 0.8);
  color: white;
  font-size: 16px;
  appearance: none;
  -webkit-appearance: none;
}

.username-input:focus {
  outline: none;
  border-color: #4caf50;
  box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
}

.start-button {
  padding: 16px 32px;
  font-size: clamp(16px, 4vw, 18px);
  border-radius: 6px;
  background-color: #4caf50;
  color: white;
  border: none;
  cursor: pointer;
  transition: background-color 0.2s;
  min-height: 44px;
  touch-action: manipulation;
}

.start-button:hover {
  background: #45a049;
}

.error-message {
  color: #ff5555;
  font-size: 14px;
  margin-top: 5px;
  width: 100%;
  text-align: left;
  margin-top: 5px;
}

@media (max-width: 768px) {
  .username-container {
    flex-direction: column;
    gap: 15px;
    align-items: center;
    padding: 0 1rem;
  }

  .username-input {
    width: 100%;
    margin: 0;
    font-size: 16px;
    min-height: 44px;
    padding: 12px 16px;
  }

  .start-button {
    width: 100%;
    max-width: 300px;
    margin-top: 10px;
    padding: 12px 24px;
  }

  .error-message {
    text-align: center;
    padding: 0 1rem;
  }
}

/* Mobile Optimizations */
@media (max-width: 768px) {
  .app-container {
    position: relative;
  }

  .start-screen {
    min-height: 100vh;
    padding-bottom: 2rem;
  }

  .tank-models-grid {
    margin: 1rem -0.5rem;
    padding: 0 0.5rem;
  }

  .tank-model-item {
    width: calc(50% - 1rem);
    /* 2 columns on mobile */
    min-width: 150px;
  }

  .tank-model-preview {
    height: 50px;
  }
}

/* Landscape Mode Optimizations */
@media (max-height: 500px) and (orientation: landscape) {
  .start-screen {
    padding: 1rem;
  }

  .content {
    margin: 0.25rem auto;
    padding: 0.5rem;
  }

  .tank-model-item {
    width: calc(33.33% - 1rem);
    /* 3 columns in landscape */
    min-width: 120px;
  }

  .tank-model-preview {
    height: 48px;
    /* Keeping aspect ratio */
  }
}
</style>
